import { enqueueSnackbar } from 'notistack';
import { useState, ReactNode, useCallback } from 'react';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { ListEntityType } from 'src/services/lists/lists.types';
import { isShadowEmail } from 'src/services/candidates/candidates.utils';
import { SignerInput, SigningOriginType } from 'src/services/signing/signing.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { toggleCandidateEditorSidesheet } from 'src/store/slices/candidate/candidateSlice';
import { CallListAddedFrom, CallListPersonType } from 'src/services/call-lists/call-lists.types';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useGetContactByIdQuery, useGetContactDocumentsQuery } from 'src/services/contacts/contacts.service';
import { openMultiMailerDialog, openSingleMailerDialog, openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';

import { RecordAction } from 'src/components/record-panel/record-panel.types';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import { useEngageAccess } from 'src/sections/engage/use-engage-access';
import AddToEngageMarketingDialog from 'src/sections/engage/add-to-engage-marketing-dialog';
import DoNotCallDialog from 'src/sections/call-lists/modals/do-not-call-dialog';
import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import AddToCallListModal from 'src/sections/call-lists/modals/add-to-call-list-modal';
import { useCallListsEnabled } from 'src/sections/call-lists/hooks/use-call-lists-enabled';
import SendForSigningModal, { SendForSigningDocument } from 'src/sections/signing/send-for-signing-modal';
import ScheduleMeetingModal, { ScheduleMeetingInitialAttendee } from 'src/sections/calendar/modal/schedule-meeting-modal';
import { buildDialNumber, useCommunicationActions } from 'src/sections/admin/integrations/communications/use-communication-actions';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

export type ContactActionKey =
  | 'send-signing'
  | 'copy-email'
  | 'send-email'
  | 'schedule-meeting'
  | 'create-candidate'
  | 'add-task'
  | 'call'
  | 'send-sms'
  | 'add-to-list'
  | 'add-to-engage-marketing'
  | 'add-to-call-list'
  | 'do-not-call';

/** What the actions need to know about one contact. */
export interface ContactActionTarget {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  doNotCall?: boolean | null;
}

interface Options {
  /**
   * `record`: one contact's own page or panel. Nothing is offered without an id, and actions that
   * don't apply to the record (no email, no phone) are hidden.
   *
   * `selection`: a list toolbar acting on the ticked rows. Add to list and Send Email take every
   * ticked row; the rest are disabled unless exactly one row is ticked. Disabled, not hidden, so the
   * menu doesn't change shape as rows are ticked.
   */
  mode: 'record' | 'selection';
  ids: string[];
  /** The contact's details when the caller already has them; skips the record fetch. */
  target?: ContactActionTarget;
  exclude?: ContactActionKey[];
  onAddedToList?: () => void;
}

type OpenModal = 'signing' | 'meeting' | 'add-to-list' | 'engage-marketing' | 'add-to-call-list' | 'do-not-call';

/**
 * The contact actions, in one list, for every place a contact can be acted on: the profile page, the
 * quick view and the list toolbar (JT1-1799). The counterpart of useCandidateActions; see it for the
 * modal rules. Render `modals` exactly once, at the caller's root, never inside a popover.
 */
export function useContactActions({ mode, ids, target, exclude, onAddedToList }: Options): {
  actions: RecordAction[];
  modals: ReactNode;
} {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();
  const canUseCallLists = useCallListsEnabled();
  const engageAccess = useEngageAccess();

  const [openModal, setOpenModal] = useState<OpenModal | null>(null);
  const closeModal = useCallback(() => setOpenModal(null), []);

  const single = ids.length === 1 ? ids[0] : undefined;

  const { currentData: fetched } = useGetContactByIdQuery(single as string, { skip: !single || !!target });

  // An id mismatch means the previous record is still in the cache; treat it as loading.
  let record: ContactActionTarget | undefined;
  if (single && target?.id === single) {
    record = target;
  } else if (single && !target && fetched?.id === single) {
    record = {
      id: fetched.id,
      firstName: fetched.first_name,
      lastName: fetched.last_name,
      email: fetched.email,
      phone: fetched.phone,
      doNotCall: fetched.do_not_call,
    };
  }

  const name = `${record?.firstName ?? ''} ${record?.lastName ?? ''}`.trim();
  const email = isShadowEmail(record?.email) ? undefined : (record?.email as string);
  const dialNumber = buildDialNumber(record?.phone);

  const comms = useCommunicationActions(
    dialNumber,
    name,
    single ? { entity_type: CommunicationEntityType.CONTACT, entity_id: single } : undefined
  );

  // Loaded only once Send for Signing is clicked; the dialog waits for them. Same arguments the
  // contact profile has always used.
  const signingDocsQuery = useGetContactDocumentsQuery(
    { contact_id: single as string, params: { page: 1, per_page: 10 } },
    { skip: !single || openModal !== 'signing' }
  );
  const signingDocsReady = !signingDocsQuery.isUninitialized && !signingDocsQuery.isFetching;

  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }, [t]);

  if (mode === 'record' && !single) {
    return { actions: [], modals: null };
  }

  const selectOne = t('record_actions.select_one_contact', { defaultValue: 'Select one contact' });
  const noEmail = t('calendar.schedule_meeting.no_email');
  const noPhone = t('record_actions.no_phone', { defaultValue: 'No phone number on this record' });

  /** Gating for an action that acts on one contact and may need a detail the record lacks. */
  const forOne = (applies: boolean, missing?: string): Pick<RecordAction, 'hidden' | 'disabled' | 'caption'> => {
    if (mode === 'record') return { hidden: !applies };
    if (!record) return { disabled: true, caption: selectOne };
    return applies ? {} : { disabled: true, caption: missing };
  };

  const isCandidateTenant = tenantType === TenantType.Candidate;
  const canEmail = featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && !isCandidateTenant;

  const handleSendEmail = () => {
    if (mode === 'selection') {
      dispatch(openMultiMailerDialog({
        targetType: MailerTargetType.CONTACT,
        templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
        entities: ids,
      }));
      return;
    }
    if (!email || !single) return;
    dispatch(openSingleMailerDialog({
      emails: [email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: single,
      targetType: MailerTargetType.CONTACT,
      targetId: single,
    }));
  };

  const actions: RecordAction[] = [
    {
      key: 'send-signing',
      label: t('common.send_for_signing', { defaultValue: 'Send for Signing' }),
      icon: 'mdi:signature-freehand',
      onClick: () => setOpenModal('signing'),
      ...forOne(true),
    },
    {
      key: 'copy-email',
      label: t('candidates.detail.actions.copy_email'),
      icon: 'mdi:email-outline',
      onClick: () => email && copy(email),
      ...forOne(!!email, noEmail),
    },
    {
      key: 'send-email',
      label: t('common.send_email'),
      icon: 'lsicon:email-send-filled',
      onClick: handleSendEmail,
      ...(mode === 'selection'
        ? { hidden: !canEmail, disabled: !ids.length }
        : { hidden: !canEmail || !email }),
    },
    {
      key: 'schedule-meeting',
      label: t('calendar.schedule_meeting.action'),
      icon: 'mdi:calendar-plus',
      onClick: () => setOpenModal('meeting'),
      ...(mode === 'record'
        ? { disabled: !email, caption: email ? undefined : noEmail }
        : forOne(!!email, noEmail)),
    },
    {
      key: 'create-candidate',
      label: t('candidates.create_candidate'),
      icon: 'hugeicons:student',
      onClick: () => dispatch(toggleCandidateEditorSidesheet({
        status: true,
        candidateId: null,
        firstName: record?.firstName ?? undefined,
        lastName: record?.lastName ?? undefined,
        email,
      })),
      ...(isCandidateTenant ? { hidden: true } : forOne(true)),
    },
    {
      key: 'add-task',
      label: t('common.add_task'),
      icon: 'ic:round-add-task',
      onClick: () => single && dispatch(openTaskEditorSidesheet({ contactId: single })),
      ...forOne(true),
    },
    {
      key: 'call',
      label: t('integrations.communications.actions.call'),
      icon: 'solar:phone-bold',
      onClick: () => comms.call(),
      // JT1-1827: nobody calls someone marked do not call
      ...(comms.available && !record?.doNotCall ? forOne(!!dialNumber, noPhone) : { hidden: true }),
      ...(comms.calling && { disabled: true }),
    },
    {
      key: 'send-sms',
      label: t('integrations.communications.actions.sms'),
      icon: 'solar:chat-round-dots-bold',
      onClick: () => comms.openSms(),
      ...(comms.available ? forOne(!!dialNumber, noPhone) : { hidden: true }),
    },
    {
      key: 'add-to-list',
      label: t('common.add_to_list', { defaultValue: 'Add to list' }),
      icon: 'solar:list-bold',
      onClick: () => setOpenModal('add-to-list'),
      disabled: !ids.length,
    },
    {
      // JT1-1925: only for people who have Engage.
      key: 'add-to-engage-marketing',
      label: t('components.engage_marketing.action'),
      icon: 'mdi:email-fast-outline',
      onClick: () => setOpenModal('engage-marketing'),
      disabled: !ids.length,
      hidden: !engageAccess,
    },
    {
      key: 'add-to-call-list',
      label: t('call_lists.add_to_call_list'),
      icon: 'solar:phone-calling-rounded-bold',
      onClick: () => setOpenModal('add-to-call-list'),
      hidden: !canUseCallLists,
      ...(mode === 'record' && record?.doNotCall
        ? { disabled: true, caption: t('call_lists.do_not_call') }
        : { disabled: !ids.length }),
    },
    {
      key: 'do-not-call',
      label: record?.doNotCall ? t('call_lists.dnc.clear') : t('call_lists.dnc.mark'),
      icon: record?.doNotCall ? 'solar:phone-calling-rounded-bold' : 'solar:forbidden-circle-bold',
      onClick: () => setOpenModal('do-not-call'),
      ...(mode === 'record' && !isCandidateTenant ? forOne(true) : { hidden: true }),
    },
  ];

  const signingDocuments: SendForSigningDocument[] = (signingDocsQuery.currentData?.results ?? []).map((d) => ({
    file_id: d.document?.file_id ?? d.document?.id ?? d.id,
    name: d.document?.name ?? d.document?.filename ?? 'Document',
    mime: d.document?.mime,
  }));

  const signers: SignerInput[] | undefined = email
    ? [{ first_name: record?.firstName ?? '', last_name: record?.lastName ?? '', email }]
    : undefined;

  const attendee: ScheduleMeetingInitialAttendee | undefined = single
    ? { type: 'contact', id: single, label: name, email }
    : undefined;

  const modals = (
    <>
      {openModal === 'signing' && single && signingDocsReady && (
        <SendForSigningModal
          open
          onClose={closeModal}
          originType={SigningOriginType.CONTACT}
          originId={single}
          documents={signingDocuments}
          defaultSigners={signers}
        />
      )}

      {openModal === 'meeting' && attendee && (
        <ScheduleMeetingModal open onClose={closeModal} initialAttendee={attendee} />
      )}

      {openModal === 'add-to-list' && (
        <AddItemsModal
          open
          entityType={ListEntityType.CONTACT}
          items={ids}
          onClose={closeModal}
          onSuccess={() => {
            closeModal();
            onAddedToList?.();
          }}
        />
      )}

      {openModal === 'engage-marketing' && ids.length > 0 && (
        <AddToEngageMarketingDialog open onClose={closeModal} personType="contact" ids={ids} personName={name || undefined} />
      )}

      {openModal === 'add-to-call-list' && (
        <AddToCallListModal
          open
          onClose={closeModal}
          personType={CallListPersonType.CONTACT}
          ids={ids}
          personName={name || undefined}
          addedFrom={mode === 'record' ? CallListAddedFrom.CONTACT_PROFILE : CallListAddedFrom.CONTACT_LIST}
        />
      )}

      {openModal === 'do-not-call' && single && (
        <DoNotCallDialog
          open
          onClose={closeModal}
          personType={CallListPersonType.CONTACT}
          personId={single}
          personName={name || undefined}
          doNotCall={!record?.doNotCall}
        />
      )}

      {comms.smsModal}
    </>
  );

  return {
    actions: exclude?.length ? actions.filter((action) => !exclude.includes(action.key as ContactActionKey)) : actions,
    modals,
  };
}
